import React, { useState, useEffect } from 'react';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { X, ClipboardCheck, ThumbsUp } from "lucide-react";

const ScrollFeedbackSurvey = ({ onComplete, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [feedback, setFeedback] = useState({
    clear: "",
    satisfaction: "",
    businessNeeds: ""
  });

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const threshold = documentHeight * 0.8;

      if (scrollPosition > threshold) {
        setIsVisible(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 sm:bottom-6 sm:inset-x-auto sm:right-6 w-full sm:w-96 bg-white rounded-t-lg sm:rounded-lg shadow-xl border border-gray-200 p-4 animate-slide-up z-50 max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-start">
        <Button variant="ghost" size="icon" onClick={onDismiss} className="h-8 w-8 absolute right-2 top-2">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="text-center mb-6 pt-2">
        <div className="flex justify-center mb-3">
          <ClipboardCheck className="h-8 w-8 text-blue-500" />
        </div>
        <h3 className="text-lg font-semibold mb-2">You're Almost Done!</h3>
        <p className="text-sm text-gray-600 px-4">
          Before submitting your project, please share your experience. Your feedback will help us improve our platform.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <Label>How clear was the process?</Label>
          <RadioGroup
            className="mt-2"
            value={feedback.clear}
            onValueChange={(value) => setFeedback(prev => ({ ...prev, clear: value }))}
          >
            {["Very clear", "clear", "Neutral", "Difficult", "Very Difficult"].map((option) => (
              <div key={option} className="flex items-center space-x-2">
                <RadioGroupItem value={option} id={`clear-${option}`} />
                <Label htmlFor={`clear-${option}`}>{option}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        <div>
          <Label>Does this process match your business workflow?</Label>
          <RadioGroup
            className="mt-2"
            value={feedback.satisfaction}
            onValueChange={(value) => setFeedback(prev => ({ ...prev, satisfaction: value }))}
          >
            {["Very much", "Yes, with some modifications", "Neutral", "No"].map((option) => (
              <div key={option} className="flex items-center space-x-2">
                <RadioGroupItem value={option} id={`satisfaction-${option}`} />
                <Label htmlFor={`satisfaction-${option}`}>{option}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        <div>
          <Label>What's missing to make this valuable for your business?</Label>
          <Textarea
            className="mt-2"
            value={feedback.businessNeeds}
            onChange={(e) => setFeedback(prev => ({ ...prev, businessNeeds: e.target.value }))}
            placeholder="Share your suggestions..."
          />
        </div>

        <div className="flex justify-end space-x-2 pt-2">
          <Button 
            variant="ghost" 
            onClick={onDismiss}
            className="text-gray-500"
          >
            Skip
          </Button>
          <Button 
            onClick={() => onComplete(feedback)} 
            disabled={!feedback.clear || !feedback.satisfaction}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <ThumbsUp className="h-4 w-4 mr-2" />
            Submit Feedback
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ScrollFeedbackSurvey;